import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { InterviewerPersona } from '@/components/admin/experiences/conversation-thread';
import {
  AddPageMenu,
  type AddPageMenuTrigger,
} from '@/components/admin/experiences/timeline/add-page-menu';
import { ConversationBadge } from '@/components/admin/experiences/timeline/conversation-badge';
import { FeedbackBadge } from '@/components/admin/experiences/timeline/feedback-badge';
import { PREVIEW_SCALE, PagePreview } from '@/components/admin/experiences/timeline/page-preview';
import type { ReaderFormat } from '@/components/reader';
import { Button, buttonVariants } from '@/components/ui/button';
import { Tooltip, TooltipShield } from '@/components/ui/tooltip';
import type { ExperienceDraft } from '@/lib/experience-draft';
import { blockCount, canRemovePage, edgeInThread } from '@/lib/experience-draft';
import { pageOffersDuplicate } from '@/lib/experience-duplicate';
import { IN_THREAD_LABEL, countLabel } from '@/lib/experience-labels';
import { PAGE_TRANSITION_LABEL, SPEED_LABEL } from '@/lib/experience-theme';
import type {
  ExperiencePageDraft,
  ExperienceVariable,
  ProtocolCard,
} from '@matter_extractor/shared';
import { EXPERIENCE_PAGES_MAX, isConversationPage } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

/**
 * La scène de la Timeline : la page sélectionnée en grand au centre, ses deux
 * voisines entières en petit, les arêtes entre elles (refonte modulaire §9.1 ;
 * maquette `Timeline.dc.html`, forme retenue et validée le 2026-09-11 ; issue
 * #57).
 *
 * « La page sélectionnée est rendue en grand au centre, comme dans le lecteur,
 * et défile dans son cadre si elle est longue ; la précédente et la suivante
 * sont entières, en petit, de part et d'autre, et servent de boutons ; les
 * arêtes portent la transition et le "+" d'insertion. »
 *
 * ## Two signs, and they are not the same sign
 *
 * The centre wears a navy RING set off from its frame (`outline` plus an
 * offset), the protocol page a full navy BORDER (§9.1, `protocol-vignette.tsx`).
 * A page can be both at once - the protocol page selected is the ordinary case
 * - so one of the two has to be a mark the other does not spend, which is why
 * the selected page is never given a border.
 *
 * ## The neighbour's click target is a SIBLING of the preview, not its parent
 *
 * The reader renders in `static` mode, which sets `inert`: an inert subtree
 * swallows the pointer event rather than letting it bubble, so a `<button>`
 * WRAPPING the preview would look pressable and do nothing. The target is
 * therefore a transparent button laid over the frame - which is also what gives
 * the neighbour its accessible name, « Page précédente : Consentement et
 * identité », where the picture itself has none.
 *
 * ## La page de retour (retour-experience.md §7.1, issue #176)
 *
 * It is always the LAST page, and the scene says so three ways: its « Retour »
 * pastille beside the name of the centre, or in the corner of the neighbour's
 * frame; no « + » after it and no button on « Fin de l'expérience », since
 * nothing is ever placed behind it; and no copy icon, like the protocol page
 * (`pageOffersDuplicate`) - while its corbeille works, the one deletion that
 * the « + » of the end can undo.
 *
 * While the experience has NONE (`onAddFeedbackPage` present), the « + » of the
 * end of the line and the « Ajouter une page » under « Fin de l'expérience »
 * open the small menu of `AddPageMenu` instead of inserting at once. Present,
 * the menu has no reason to be: the « + » before the feedback page is a one-click
 * insertion of a content page, `insertPageAt` clamping it before the retour.
 *
 * ## Why the whole row scrolls sideways inside its own box
 *
 * At the screen format the row is 160 + 64 + 640 + 64 + 160 pixels wide before
 * its padding, which fits a laptop and fits nothing narrower - and
 * `/admin/experiences/nouveau` is one of the addresses `admin-responsive.spec.ts`
 * holds to zero horizontal body scroll. The rule of the root `CLAUDE.md` applies
 * as written: wide content scrolls in its own `overflow-x` container, the page
 * body never does. `mx-auto` on the row centres it whenever it does fit; when
 * it does not, an auto margin resolves to zero and the row simply starts at the
 * left, which is where a scroll has to start.
 */

export interface ExperienceSceneProps {
  readonly draft: ExperienceDraft;
  /** The page `?page=` names, already resolved by `selectedPage`. */
  readonly selected: ExperiencePageDraft;
  readonly format: ReaderFormat;
  readonly readOnly: boolean;
  /** The entity's id, or null before the first save - « Éditer » has no address then. */
  readonly experienceId: string | null;
  /** The protocol library, for the protocol page's card (§9.5). */
  readonly protocols: readonly ProtocolCard[];
  readonly protocolsRead: boolean;
  readonly onSelectPage: (pageId: string) => void;
  /** A « + » on an edge: the GAP to fill, `0` being « before the first page ». */
  readonly onInsert: (index: number) => void;
  readonly onDelete: (pageId: string) => void;
  /** « Dupliquer » - the copy lands just after its source and is selected (§4.2). */
  readonly onDuplicate: (pageId: string) => void;
  /** Why the copy icon is inert, or null when it is live - the ceiling of §4.2. */
  readonly duplicateRefusal: string | null;
  /** « Remplacer » on the protocol page - the tab opens the selector (§9.5). */
  readonly onReplaceProtocol: () => void;
  /** What a variable is NAMED on the scene - the pill shows the name, never a value (D5). */
  readonly variableNames: (variable: ExperienceVariable) => string;
  /**
   * « Page de retour » of the menu of the end (§7.1). PRESENT only while the
   * experience has no feedback page: then the « + » of the end opens the menu
   * « Page de contenu » / « Page de retour ». Absent, those two gestures insert
   * a content page at once.
   */
  readonly onAddFeedbackPage?: (() => void) | undefined;
  /** Why « Page de retour » is closed - the ceiling of 30 pages - or null (§7.1). */
  readonly feedbackRefusal?: string | null | undefined;
}

/** « fondu · normal » - what an edge says about the transition it carries (§9.3). */
function transitionLabel(page: ExperiencePageDraft): string {
  const { animation, speed } = page.transition;
  if (animation === 'none') return PAGE_TRANSITION_LABEL.none;
  return `${PAGE_TRANSITION_LABEL[animation]} · ${SPEED_LABEL[speed]}`;
}

/** « 6 blocs », « durée 6 s », « Protocole choisi » - the pastille under the centre. */
function pastille(page: ExperiencePageDraft): string {
  if (page.kind === 'protocol')
    return page.protocol === null ? 'sans protocole' : 'protocole choisi';
  if (page.autoDurationSeconds !== null) return `durée ${page.autoDurationSeconds} s`;
  return countLabel(blockCount(page), 'bloc');
}

/** The round « + » of an edge: a plain button, or the trigger of the menu of the end. */
function PlusButton({
  label,
  onClick,
  menu,
}: {
  label: string;
  onClick?: () => void;
  menu?: AddPageMenuTrigger;
}) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        data-testid={EXPERIENCE_IDS.insertPage}
        aria-label={label}
        {...menu}
        onClick={menu === undefined ? onClick : menu.onClick}
        className="inline-flex size-7 cursor-pointer items-center justify-center rounded-full border-2 border-bleu-nuit bg-white text-bleu-nuit hover:bg-bleu-nuit hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Plus aria-hidden="true" className="size-3.5" />
      </button>
    </Tooltip>
  );
}

/**
 * The 84 px rule of an edge, its transition pastille, and what stands under it:
 * a « + » (`plus`), or nothing - after the feedback page, and in read-only.
 */
function Edge({
  transition,
  inThread = false,
  plus,
}: {
  transition: string | null;
  /** The edge stays in the thread: « dans le fil », the line and its pastille in navy (§6). */
  inThread?: boolean;
  plus: ReactNode;
}) {
  return (
    <div
      data-testid={EXPERIENCE_IDS.sceneEdge}
      data-in-thread={inThread ? 'true' : 'false'}
      /* `z-10`, because the pastille is WIDER than the 64 px edge it is centred
         on: « fondu · normal » spills over both neighbours, and the one after it
         in the DOM would otherwise paint over the half that spills right. */
      className="relative z-10 flex w-16 shrink-0 flex-col items-center gap-2.5"
    >
      <div className={`relative h-0.5 w-14 ${inThread ? 'bg-bleu-nuit' : 'bg-rail'}`}>
        {transition === null ? null : (
          <span
            data-testid={EXPERIENCE_IDS.sceneTransition}
            className={`-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 whitespace-nowrap rounded-pill border bg-white px-2 text-xs leading-4 ${
              inThread ? 'border-bleu-nuit text-bleu-nuit' : 'border-gray-200 text-gray-600'
            }`}
          >
            {transition}
          </span>
        )}
      </div>
      {plus}
    </div>
  );
}

/**
 * La scène, sous le persona qui nomme la bulle d'un aperçu en fil
 * (`conversation-thread.tsx`) : un composant à part pour ne poser le contexte qu'une
 * fois, au-dessus de la page du centre ET de ses deux voisines.
 */
export function ExperienceScene(props: ExperienceSceneProps) {
  return (
    <InterviewerPersona personaId={props.draft.persona?.ref.id ?? null}>
      <ExperienceSceneInner {...props} />
    </InterviewerPersona>
  );
}

function ExperienceSceneInner({
  draft,
  selected,
  format,
  readOnly,
  experienceId,
  protocols,
  protocolsRead,
  onSelectPage,
  onInsert,
  onDelete,
  onDuplicate,
  duplicateRefusal,
  onReplaceProtocol,
  variableNames,
  onAddFeedbackPage,
  feedbackRefusal = null,
}: ExperienceSceneProps) {
  /** Which page the corbeille is asking about - see « la confirmation » below. */
  const [confirming, setConfirming] = useState<string | null>(null);

  const index = draft.pages.findIndex((one) => one.id === selected.id);
  const previous = index <= 0 ? null : (draft.pages[index - 1] ?? null);
  const next = draft.pages[index + 1] ?? null;
  const scale = PREVIEW_SCALE[format];

  const cardOf = (page: ExperiencePageDraft): ProtocolCard | null =>
    page.protocol === null ? null : (protocols.find((one) => one.id === page.protocol?.id) ?? null);

  const preview = (page: ExperiencePageDraft, at: number) => (
    <PagePreview
      page={page}
      theme={draft.theme}
      format={format}
      scale={at}
      protocolCard={cardOf(page)}
      protocolsRead={protocolsRead}
      variableNames={variableNames}
    />
  );

  /** A neighbour: the whole page in small, with a chevron and its name (§9.1). */
  const neighbour = (page: ExperiencePageDraft, side: 'previous' | 'next') => (
    <div
      data-testid={EXPERIENCE_IDS.sceneNeighbour}
      data-side={side}
      data-page-id={page.id}
      data-kind={page.kind}
      className="flex shrink-0 flex-col gap-2"
    >
      <div
        className={`relative overflow-hidden rounded-md border-2 ${
          isConversationPage(page) ? 'border-bleu-nuit' : 'border-gray-200'
        }`}
      >
        {preview(page, scale.neighbour)}
        {page.kind === 'feedback' ? <FeedbackBadge placement="frame" /> : null}
        {isConversationPage(page) ? <ConversationBadge placement="frame" /> : null}
        {/* Over the preview and not around it - the reader is `inert`, see the head. */}
        <button
          type="button"
          onClick={() => onSelectPage(page.id)}
          className="absolute inset-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="sr-only">
            {`${side === 'previous' ? 'Page précédente' : 'Page suivante'} : ${page.name}`}
          </span>
        </button>
      </div>
      <p
        className={`flex items-center gap-1 font-semibold text-bleu-nuit text-sm leading-5 ${
          side === 'previous' ? '' : 'justify-end'
        }`}
      >
        {side === 'previous' ? <ChevronLeft aria-hidden="true" className="size-3.5" /> : null}
        <span className="min-w-0 truncate">{page.name}</span>
        {side === 'next' ? <ChevronRight aria-hidden="true" className="size-3.5" /> : null}
      </p>
    </div>
  );

  /**
   * The menu of the end (§7.1): its two entries land at the END of the line - a
   * content page through the same insertion as the « + » (`onInsert`), the
   * feedback page through `onAddFeedbackPage`. Null unless that is asked.
   */
  const endMenu = (trigger: (props: AddPageMenuTrigger) => ReactNode) =>
    onAddFeedbackPage === undefined ? null : (
      <AddPageMenu
        label="Ajouter une page"
        contentHint={`Une page de blocs, après « ${selected.name === '' ? 'Page sans nom' : selected.name} »`}
        contentRefusal={
          draft.pages.length >= EXPERIENCE_PAGES_MAX
            ? `L’expérience porte déjà ${EXPERIENCE_PAGES_MAX} pages, le maximum : supprime-en une pour en ajouter une autre.`
            : null
        }
        feedbackRefusal={feedbackRefusal}
        onContent={() => onInsert(draft.pages.length)}
        onFeedback={onAddFeedbackPage}
        trigger={trigger}
      />
    );

  /**
   * « Début » and « Ajouter une page » - what stands where a neighbour does not.
   *
   * `action` is a function for the one-click gesture, `menu` for the small menu
   * of §7.1, and null for no button at all.
   */
  const endCap = (label: string, action: (() => void) | 'menu' | null) => (
    <div className="flex w-36 shrink-0 flex-col items-center gap-2">
      <div className="flex h-24 w-full items-center justify-center rounded-md border-2 border-gray-200 border-dashed bg-white px-3 text-center text-gray-600 text-sm leading-5">
        {label}
      </div>
      {action === null ? null : action === 'menu' ? (
        endMenu((props) => (
          <Button type="button" variant="outline" size="sm" {...props}>
            <Plus aria-hidden="true" className="size-4" />
            Ajouter une page
          </Button>
        ))
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={action}>
          <Plus aria-hidden="true" className="size-4" />
          Ajouter une page
        </Button>
      )}
    </div>
  );

  /*
    What stands after the centre (§7.1). On the feedback page: nothing - no « + »
    and no button, since nothing is ever placed behind it. At the end of a line
    that has none yet: the menu. Anywhere else: the ordinary insertion, which
    `insertPageAt` clamps BEFORE the feedback page.
  */
  const afterFeedback = selected.kind === 'feedback';
  const menuAtEnd = onAddFeedbackPage !== undefined && next === null && !afterFeedback;
  const afterLabel =
    next === null
      ? `Insérer une page après « ${selected.name} »`
      : `Insérer une page entre « ${selected.name} » et « ${next.name} »`;

  const removable = canRemovePage(draft, selected.id);
  const empty = selected.kind === 'content' && blockCount(selected) === 0;

  return (
    <div
      data-testid={EXPERIENCE_IDS.scene}
      className="overflow-x-auto rounded-md border border-gray-200 bg-gray-50 p-5"
    >
      <div className="mx-auto flex w-max items-center gap-0">
        {previous === null
          ? endCap('Début de l’expérience', null)
          : neighbour(previous, 'previous')}

        <Edge
          transition={
            previous === null
              ? null
              : edgeInThread(draft.pages, index)
                ? IN_THREAD_LABEL
                : transitionLabel(previous)
          }
          inThread={previous !== null && edgeInThread(draft.pages, index)}
          plus={
            readOnly ? null : (
              <PlusButton
                label={
                  previous === null
                    ? `Insérer une page avant « ${selected.name} »`
                    : `Insérer une page entre « ${previous.name} » et « ${selected.name} »`
                }
                onClick={() => onInsert(index)}
              />
            )
          }
        />

        <div
          data-testid={EXPERIENCE_IDS.scenePage}
          data-page-id={selected.id}
          data-kind={selected.kind}
          data-format={format}
          className="flex shrink-0 flex-col gap-2.5"
        >
          {/* The ring of §9.1, offset from the frame and never a border: the
              full navy border is the protocol page's own sign. */}
          <div className="overflow-hidden rounded-md border-2 border-gray-200 shadow-lg outline-2 outline-bleu-nuit outline-offset-4">
            {preview(selected, scale.centre)}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2.5">
              <p className="min-w-0 break-words font-semibold text-base text-bleu-nuit leading-6">
                {selected.name === '' ? 'Page sans nom' : selected.name}
              </p>
              {selected.kind === 'feedback' ? <FeedbackBadge /> : null}
              {isConversationPage(selected) ? <ConversationBadge /> : null}
              <span className="shrink-0 rounded-pill bg-gray-100 px-2.5 py-0.5 font-semibold text-gray-700 text-xs leading-4">
                {pastille(selected)}
              </span>
              <span className="shrink-0 text-gray-500 text-xs leading-4">
                {`page ${index + 1} sur ${draft.pages.length}`}
              </span>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {selected.kind === 'protocol' && !readOnly ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid={EXPERIENCE_IDS.protocolReplace}
                  onClick={onReplaceProtocol}
                >
                  Remplacer
                </Button>
              ) : null}

              {experienceId === null ? null : (
                <Link
                  to="/admin/experiences/$experienceId/pages/$pageId"
                  params={{ experienceId, pageId: selected.id }}
                  data-testid={EXPERIENCE_IDS.editPage}
                  className={`${buttonVariants({ variant: 'outline', size: 'sm' })} no-underline`}
                >
                  <Pencil aria-hidden="true" className="size-4" />
                  Éditer
                </Link>
              )}

              {/*
                L'icône de copie, DANS LA GOUTTIÈRE ET JUSTE AVANT LA CROIX
                (§4.2) - la forme du geste, la même sur les sept items.

                Absente en lecture seule comme la croix, et absente sur la page
                protocole, que §9.1 veut obligatoire et unique : `pageOffersDuplicate`
                porte cette décision pour les deux écrans. Elle reste offerte sur
                une page que la croix refuse de supprimer (`canRemovePage` garde
                la dernière page de contenu), parce que dupliquer la seule page
                d'une expérience est précisément le geste qui en donne une seconde.

                Au plafond, le bouton est VRAIMENT `disabled` et c'est
                `TooltipShield` qui le rend survolable : §4.2 veut que l'icône
                « dise pourquoi au survol », or un bouton `disabled` n'émet aucun
                événement de pointeur et sa bulle ne s'ouvrirait jamais. C'est
                exactement le cas pour lequel l'enveloppe existe, et `Button`
                porte déjà `DISABLED_INERT` - donc rien à peindre ici, et surtout
                pas une opacité, qui pâlit un bouton sans le rendre inerte.

                `describes` est laissé au composant : il retire de lui-même la
                description qui répéterait le nom accessible, et la garde quand
                la bulle porte la raison du refus, qui en diffère.
              */}
              {readOnly || !pageOffersDuplicate(selected) ? null : (
                <Tooltip label={duplicateRefusal ?? `Dupliquer la page « ${selected.name} »`}>
                  <TooltipShield closed={duplicateRefusal !== null}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={EXPERIENCE_IDS.duplicatePage}
                      aria-label={`Dupliquer la page « ${selected.name} »`}
                      disabled={duplicateRefusal !== null}
                      onClick={() => onDuplicate(selected.id)}
                    >
                      <Copy aria-hidden="true" className="size-4" />
                    </Button>
                  </TooltipShield>
                </Tooltip>
              )}

              {/*
                La confirmation en ligne, et seulement quand la page porte
                quelque chose. A page with no block is a page an operator added
                by mistake a second ago, and asking twice for it teaches them to
                click through the question; a page with six blocks is work, and
                a single click is how it disappears. « Aucune corbeille » on the
                protocol page is `canRemovePage`, which also keeps the last page
                of content.
              */}
              {readOnly || !removable ? null : confirming === selected.id ? (
                <span className="flex items-center gap-2 text-gray-700 text-sm">
                  Supprimer cette page ?
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    data-testid={EXPERIENCE_IDS.deletePageConfirm}
                    onClick={() => {
                      setConfirming(null);
                      onDelete(selected.id);
                    }}
                  >
                    Supprimer
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirming(null)}
                  >
                    Annuler
                  </Button>
                </span>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid={EXPERIENCE_IDS.deletePage}
                  aria-label={`Supprimer la page « ${selected.name} »`}
                  onClick={() => (empty ? onDelete(selected.id) : setConfirming(selected.id))}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <Edge
          transition={
            next === null
              ? null
              : edgeInThread(draft.pages, index + 1)
                ? IN_THREAD_LABEL
                : transitionLabel(selected)
          }
          inThread={next !== null && edgeInThread(draft.pages, index + 1)}
          plus={
            readOnly || afterFeedback ? null : menuAtEnd ? (
              endMenu((props) => <PlusButton label={afterLabel} menu={props} />)
            ) : (
              <PlusButton label={afterLabel} onClick={() => onInsert(index + 1)} />
            )
          }
        />

        {next === null
          ? endCap(
              'Fin de l’expérience',
              readOnly || afterFeedback
                ? null
                : menuAtEnd
                  ? 'menu'
                  : () => onInsert(draft.pages.length),
            )
          : neighbour(next, 'next')}
      </div>
    </div>
  );
}
